import { Checkbox, Form, Radio } from 'antd';
import { FloatInput } from '../../../components/Fields/FloatInput';
import { FloatSelect } from '../../../components/Fields/FloatSelect';
import { FloatTextArea } from '../../../components/Fields/FloatTextArea';
import i from '../../../assets/i.svg';

import './index.scss';
import { QuestionAnswer } from '..';

interface PropsI {
    component?: any;
    field: QuestionAnswer;
    required: boolean;
    updateAnswer?: any;
    saveData: QuestionAnswer[];
    component_id: number | null;
}

export const MultipleChoice = ({ component, field, required, updateAnswer, saveData, component_id }: PropsI) => {
    const element = saveData?.find((elem) => {
        const matchById = elem.id + '' === field.id + '';

        if (field.component_id != null) {
            return matchById && elem.component_id + '' === field.component_id + '';
        }

        return matchById;
    });

    return (
        <div className="Field">
            {component && <div className="Field__component">{component}</div>}
            <div className="Field__title">
                {field.name}
                {required && <span className="red">*</span>}
            </div>
            <div className="Field__description">{field.description}</div>
            <div className="Field__options">
                {field?.options?.map((option: any) => {
                    return (
                        <div className="Field__option">
                            <Checkbox
                                onClick={() => updateAnswer(field.id, option?.label, field.type, component_id)}
                                checked={element?.answer?.includes(option?.label)}
                                className={element?.error ? 'red' : ''}
                            />
                            {option?.label}
                        </div>
                    );
                })}
                {element?.error && (
                    <div className="error-text">{element?.errorMessage || 'This field is required'}</div>
                )}
            </div>
        </div>
    );
};

export const ShortAnswer = ({ field, required, updateAnswer, saveData }: PropsI) => {
    const row = saveData?.find((elem) => elem.id + '' === field.id + '');
    const answer = row?.answer;
    const error = row?.error;
    const errorMessage = row?.errorMessage;

    return (
        <div className="Field">
            <div className="Field__input">
                <FloatInput
                    floatLabel={field.name}
                    className="input"
                    required={required}
                    onChange={(e: any) => updateAnswer(field.id, e.target.value, field.type, null)}
                    value={answer}
                    hasError={() => !!error}
                    maxLength={60}
                    style={{ width: '600px' }}
                />
                {error && <div className="error-text">{errorMessage || 'This field is required'}</div>}
            </div>
            <div className="Field__description">{field.description}</div>
        </div>
    );
};

export const Paragraph = ({ field, required, updateAnswer, saveData }: PropsI) => {
    const row = saveData?.find((elem) => elem.id + '' === field.id + '');
    const answer = row?.answer;
    const error = row?.error;
    const errorMessage = row?.errorMessage;

    return (
        <div className="Field">
            <div className="Field__input">
                <FloatTextArea
                    floatLabel={field.name}
                    className="input"
                    hasError={() => !!error}
                    required={required}
                    onChange={(e: any) => updateAnswer(field.id, e.target.value, field.type, null)}
                    value={answer}
                />
                {error && <div className="error-text">{errorMessage || 'This field is required'}</div>}
            </div>
            <div className="Field__description">{field.description}</div>
        </div>
    );
};

export const SingleSelect = ({ component, field, required, updateAnswer, saveData, component_id }: PropsI) => {
    const element = saveData?.find((elem) => {
        const matchById = elem.id + '' === field.id + '';

        if (field.component_id != null) {
            return matchById && elem.component_id + '' === field.component_id + '';
        }

        return matchById;
    });

    return (
        <div className="Field">
            {component && <div className="Field__component">{component}</div>}
            <div className="Field__title">
                {field.name}
                {required && <span className="red">*</span>}
            </div>
            <div className="Field__description">{field.description}</div>
            <div className="Field__options">
                {field?.options?.map((option: any) => {
                    return (
                        <div className="Field__option" key={String(option?.label ?? option?.value ?? '')}>
                            <Radio
                                onClick={() => updateAnswer(field.id, option?.label, field.type, component_id)}
                                checked={element?.answer?.includes(option?.label)}
                                className={element?.error ? 'red' : ''}
                            />
                            {option?.label}
                        </div>
                    );
                })}
                {element?.error && (
                    <div className="error-text">
                        {element?.errorMessage ||
                            'This question is required. Please select one of the options'}
                    </div>
                )}
            </div>
        </div>
    );
};

/** Same as `SingleSelect`; root class scoped for Score Card page styles only. */
export const SingleSelectScorecard = ({ component, field, required, updateAnswer, saveData, component_id }: PropsI) => {
    const element = saveData?.find((elem) => {
        const matchById = elem.id + '' === field.id + '';

        if (field.component_id != null) {
            return matchById && elem.component_id + '' === field.component_id + '';
        }

        return matchById;
    });

    return (
        <div className="Field score-card-single-select">
            {component && <div className="Field__component">{component}</div>}
            <div className="Field__title">
                {field.name}
                {required && <span className="red">*</span>}
            </div>
            <div className="Field__description">{field.description}</div>
            <div className="Field__options">
                {field?.options?.map((option: any) => (
                    <div className="Field__option" key={String(option?.label ?? option?.value ?? '')}>
                        <Radio
                            onClick={() => updateAnswer(field.id, option?.label, field.type, component_id)}
                            checked={element?.answer === option?.label}
                            className={element?.error ? 'red' : ''}
                        />
                        {option?.label}
                    </div>
                ))}
                {element?.error && (
                    <div className="error-text">
                        {element?.errorMessage ||
                            'This question is required. Please select one of the options'}
                    </div>
                )}
            </div>
        </div>
    );
};

export const Dropdown = ({ component, field, required, updateAnswer, saveData, component_id }: PropsI) => {
    const element = saveData?.find((elem) => {
        const matchById = elem.id + '' === field.id + '';

        if (field.component_id != null) {
            return matchById && elem.component_id + '' === field.component_id + '';
        }

        return matchById;
    });

    const selectedDropdownLabel = Array.isArray(element?.answer) ? element?.answer[0] : element?.answer;

    return (
        <div className="Field">
            {component && <div className="Field__component">{component}</div>}

            <div>
                <Form>
                    <Form.Item className="input-wrapper dropdown-sign-supplier" style={{ margin: '10px 0 0 0' }}>
                        <FloatSelect
                            required={required}
                            floatLabel={field?.name}
                            err={!!element?.error}
                            setSearch={() => {}}
                            options={field?.options?.map((elem: any) => ({ name: elem.label, value: elem.label }))}
                            onChange={(value: any) => updateAnswer(field.id, value?.name, field.type, component_id)}
                            search={''}
                            form={undefined}
                            hasError={() => !!element?.error}
                            type="common"
                            value={
                                selectedDropdownLabel
                                    ? { name: selectedDropdownLabel, label: selectedDropdownLabel }
                                    : ''
                            }
                            style={{ maxWidth: '600px' }}
                        />
                    </Form.Item>
                    {element?.error && (
                        <div className="error-text" style={{ margin: '2px 0 0 0' }}>
                            {element?.errorMessage || 'This field is required'}
                        </div>
                    )}
                </Form>
                <div className="Field__description">{field.description}</div>
            </div>
        </div>
    );
};

export const ExplanationBanner = ({ field }: PropsI) => {
    return (
        <div className="note">
            <img src={i} />
            {field?.description}
        </div>
    );
};
